/**
 * /learn touch-market example (#786): two made-up price paths from listing (index 0) to the deadline (last index) and
 * the rule that decides a priceTouch market, simplified to whole steps: the market is touched at the first step where
 * the `twap`-step trailing average of the price equals or crosses the target. Pure; mirrored by the native Learn screens
 * through shared/fixtures/learn.json (fn "touchPathResult").
 */

/** Illustrative paths (not real data), 41 steps, both starting at 100. */
export const TOUCH_PATHS = {
  /** A spike early on: touches a target of 110 at step 14 (the single wick at step 13 is not enough on its own). */
  early: [
    100.0, 101.2, 101.7, 101.3, 100.5, 100.1, 100.6, 101.8, 103.0, 103.5, 103.5, 104.2, 107.5, 112.9, 116.5, 115.3, 110.8, 106.8,
    104.4, 103.6, 104.1, 105.3, 106.5, 106.9, 106.5, 105.7, 105.3, 105.9, 107.1, 108.2, 108.6, 108.2, 107.4, 107.1, 107.6, 108.9,
    110.0, 110.4, 109.9, 109.1, 108.8,
  ],
  /** A slow drift that stays below 110 until the deadline. */
  never: [
    100.0, 101.8, 102.0, 101.6, 101.2, 101.1, 101.6, 102.6, 103.8, 104.8, 105.2, 105.1, 104.5, 103.8, 103.5, 103.9, 104.7, 105.7,
    106.6, 106.8, 106.5, 105.8, 105.1, 104.7, 104.9, 105.7, 106.6, 107.4, 107.6, 107.2, 106.4, 105.6, 105.2, 105.4, 106.2, 107.1,
    107.8, 108.0, 107.5, 106.7, 105.9,
  ],
} as const;

export type TouchPathKey = keyof typeof TOUCH_PATHS;

/** Steps in the example's TWAP window. */
export const TOUCH_EXAMPLE_TWAP = 3;

export interface TouchPathResult {
  /** "up" when the target is above the starting price, "down" when below, "at" when equal (touched at step 0). */
  direction: 'up' | 'down' | 'at';
  /** First step whose trailing average equals or crosses the target; null when never touched. */
  touchedAt: number | null;
  /** Yes when touched, else No (at the deadline). */
  result: 'yes' | 'no';
  /** Touched strictly before the deadline (the last step). */
  early: boolean;
  /** Steps left before the deadline at the touch (0 when not touched). */
  stepsEarly: number;
}

/**
 * Touch result of a path. avg(i) = mean of path[max(0, i - twap + 1) .. i] (summed left to right, divided by the count,
 * IEEE doubles). up: touched when avg(i) >= target; down: when avg(i) <= target. twap < 1 is treated as 1.
 * Throws on an empty path or a non-finite target.
 */
export function touchPathResult(path: readonly number[], target: number, twap: number): TouchPathResult {
  if (path.length === 0 || !Number.isFinite(target)) throw new Error('touchPathResult: empty path or bad target');
  const w = Math.max(1, Math.floor(twap));
  const start = path[0]!;
  const last = path.length - 1;
  if (target === start) return { direction: 'at', touchedAt: 0, result: 'yes', early: last > 0, stepsEarly: last };
  const up = target > start;
  for (let i = 0; i <= last; i++) {
    const from = Math.max(0, i - w + 1);
    let sum = 0;
    for (let j = from; j <= i; j++) sum += path[j]!;
    const avg = sum / (i - from + 1);
    if (up ? avg >= target : avg <= target) {
      return { direction: up ? 'up' : 'down', touchedAt: i, result: 'yes', early: i < last, stepsEarly: last - i };
    }
  }
  return { direction: up ? 'up' : 'down', touchedAt: null, result: 'no', early: false, stepsEarly: 0 };
}
